import { AuthService, userSelectors } from '@skinsoft/auth';
import { fetch, Proxy } from '@skinsoft/network';
import { identity, isEqual, merge as lodashMerge } from 'lodash';
import moment from 'moment';
import { EMPTY, from, iif, merge, Observable, of, ReplaySubject, Subject, Subscription, throwError, timer, zip } from 'rxjs';
import {
    catchError,
    concatMap,
    delayWhen,
    distinctUntilChanged,
    filter,
    first,
    last,
    map,
    mergeMap,
    pluck,
    retryWhen,
    switchMap,
    take,
    tap,
} from 'rxjs/operators';
import { DataSynchronisationStatus, OfflineDataFetcher } from './offline/offline-data-fetcher'

import { OfflineDataUpdateCacher } from '@skinsoft/data-store/src/offline/offline-data-update-cacher';
import { MediaTransferHelper } from '@skinsoft/media-transfer-helper';
import _ from 'lodash';
import { IndexedDBCache } from './cache/indexeddb-cache';
import { Cache } from './cache/types';

type FragmentParser<T> = (response: any) => T[];

declare const FALLBACK_LOCALE;

export interface SearchResponse<T> {
    totalSize: number,
    allData: T[],
    results: T[]
}

export interface DataSynchroEvent {
    status: DataSynchronisationStatus,
    name: string
}

interface CachedEntityData<T> {
    id: string;
    body: T;
}

export const dataSynchroEventBus$ = new Subject<DataSynchroEvent>();

const SEARCH_PAGE_SIZE: number = 100;

export interface PushResult {
    nbToPush: number;
    nbPushSuccesses: number;
}

export interface DataStoreOptions {
    endpoint?: string;
    query?: Record<string, any>;
    emulateSearch?: boolean;
}

export const DEFAULT_PROXY = new Proxy();
/**
 * Base class for all data management in the app.
 * Generics : 
 *  - T : principal type of the entity managed by this class
 *  - S : secondary type retrieved when performing a search query (if not specified, it is equals to T by default)
 */
export abstract class DataStore<T = any, S = T> {
    public userSelectors = userSelectors;

    protected synchronizationMap: {[key: string]: string} = {}

    public get sanitizedIdentifer() {
        // For a strange reason, 'replaceAll' doesn't exist in Chrome for now, so we cheat
        const sanitized = this.endpoint.split('/').join('');
        return sanitized
    }

    public get getNbEntitiesInUpdateCache() {
        return this.nbEntitiesInUpdateCache
    }

    public get getNbLocallyCreatedEntities() {
        return this.nbCreatedEntitiesInUpdateCache;
    }

    public get isSyncing() {
        return this.syncing;
    }

    private get mainCacheKey() {
        return this.endpoint;
    }

    public get lastSyncStats() {
        return this.offlineDataFetcher.getLastSynchroStats();
    }

    protected proxy = DEFAULT_PROXY;
    protected endpoint: string;
    protected cache: Cache<T>;

    //// OFFLINE //////////////////////////////////
    protected offlineDataFetcher: OfflineDataFetcher<T, S>;
    protected offlineDataUpdateCacher: OfflineDataUpdateCacher<T>;
    protected emulateSearch: boolean = false;

    // Stats
    private currentNbSychronized: number = 0;
    private maxNbToSynchronize: number = Infinity;
    private fragmentParser: FragmentParser<T>;
    private userGroupId: string;
    private typesForSearch: string[]

    // Offline mode 
    private globalPushing = false;

    private dataStore: DataStore<T>;
    private syncing: boolean = false;
    private pushing: boolean = false;
    private pushresult: PushResult = { nbPushSuccesses: 0, nbToPush: 0 };
    private nbEntitiesInUpdateCache: number = 0;
    private nbCreatedEntitiesInUpdateCache: number = 0;

    private lastSynchroStats: any = null;
    private lastPushDate: number = 0;


    private executionStartTime: number = 0;

    private currentSynchroSubscription: Subscription = null as any;

    private abortSync: boolean = false;

    constructor(
        endpoint: string,
        typesForSearch?: string[],
        fragmentParser: FragmentParser<T> = identity,
        proxy = DEFAULT_PROXY,
        cache: Cache<T> = new IndexedDBCache<T>(),
    ) {
        this.fragmentParser = fragmentParser;
        this.endpoint = endpoint;
        this.cache = cache;
        this.proxy = proxy;
        if (typesForSearch) {
            this.typesForSearch = typesForSearch;
        }

        // Fetching user group
        AuthService.getInstance().user$.pipe(
            switchMap((user) => {
                if (user) {
                    return this.proxy.fetch("/groups/userGroup", {
                        userId: user.id,
                        workspace: (localStorage.getItem("workspace") || userSelectors.defaultWorkspace(user).id),
                    })
                } else {
                    return EMPTY;
                }
            })
        ).subscribe((group) => {
            this.userGroupId = group.id;
        });

        // Offline mode
        this.initializeDataFetcher();
        this.initializeDataPusher();

    }

    /**
     * @deprecated This method fetches all data of one type. This can lead to performance issues 
     * or crash if data is very numerous. The api endpoint will probably be removed in future.
     * @param options 
     * @param method 
     */
    public fetchAll(options: DataStoreOptions = {}, method = 'GET'): Observable<T[]> {

        const searchParams = new URLSearchParams(options.query);
        searchParams.set('archived', 'false');

        const endpoint = options.endpoint || this.endpoint;
        const cacheKey = `${endpoint}?${searchParams.toString()}`;

        return merge(
            this.proxy.fetch(endpoint, method === 'GET' ? searchParams : undefined, method, method !== 'GET' ? options.query : undefined).pipe(
                map(this.fragmentParser),
                // tap(data => this.cache.write(cacheKey, data)),
                catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
            ),
            // this.cache$(cacheKey),
        ).pipe(distinctUntilChanged(isEqual));
    }

    public fetchOne(id: string, networkFirst = false): Observable<T> {
        // if online
        if (window.navigator.onLine && !OfflineDataFetcher.isTemporaryId(id)) {
            const uri = `${this.endpoint}/${id}`;

            const network$ = this.proxy.fetch(uri, undefined, undefined, undefined, networkFirst).pipe(
                tap(data => this.offlineDataFetcher.updateOne(data.id, data)),
                last(),
                catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
            );
            let source$ = network$;

            if (networkFirst) {
                source$ = source$.pipe(delayWhen(() => network$));
            }

            return source$.pipe(
                distinctUntilChanged(isEqual),
                this.mergeUpdateCacheDataToOneResult.bind(this),
            );
        }
        // if offline
        else {
            return this.fetchOneOfflineMode(id);
        }
    }

    public fetchOneOfflineMode(id: string): Observable<T> {
        return iif(
            () => OfflineDataFetcher.isTemporaryId(id),
            this.offlineDataUpdateCacher.fetchOneFromId(id),
            this.offlineDataFetcher.fetchOneFromPredicate((e) => _.get(e, 'id') === id)
        ).pipe(
            switchMap(async (data: any) => {
                // merging values from update-cache with value from fetch-cache not to loose information
                const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(id);
                const theMerge = lodashMerge(await data, cachedUpdatedValue)
                return theMerge;
            }
            ),
        );
    }

    /**
     * This method uses the searchAll method with an different endpoint to use the old search way, useful to
     * retrieve paged data to keep in offline cache
     * @param queryText 
     * @param sortObject 
     * @param pageSize 
     * @param types 
     * @param page 
     * @param advancedSearches 
     * @param filteredIds 
     * @param pathAndValueToCheck 
     * @param options 
     */
    public synchronizeAll(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, options: DataStoreOptions = {}): Observable<SearchResponse<S>> {
        return this.searchAll(queryText, sortObject, pageSize, types, page, advancedSearches, filteredIds, pathAndValueToCheck, "/indexSearch/dbSearch", options);
    }

    public searchAll(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, options: DataStoreOptions = {})
        : Observable<SearchResponse<S>> {
        const endpoint = searchEndpointOverride || "/indexSearch/search";
        const searchTypes = types || this.typesForSearch // trying to get type name from endpoint (as they are suposed to be similar)
        const query = {
            queryText,
            sortObject,
            types: searchTypes,

            advancedSearches,
            excludedIds: null,
            filteredIds,
            group: this.userGroupId,
            language: FALLBACK_LOCALE,
            page,
            pageSize,
            pathAndValueToCheck,
            searchOnArchived: false,
            searchOnVersioned: false
        }
        const cacheKey = `${endpoint}?${searchTypes.toString()}?${query.toString()}`;
        // if online
        if (this.offlineDataFetcher && window.navigator.onLine) {
            return this.proxy.fetch(endpoint, undefined, 'POST', query).pipe(
                // tap(data => this.cache.write(cacheKey, data)),
                catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
                this.mergeUpdateCacheDataToSearchResults.bind(this),
                // Adding locally created entities to results
                switchMap( // TODO can be improved with sorting cached values and online values together
                    async data => {
                        let localEntities = await this.getLocallyCreatedCachedObjectsAsS();
                        if (localEntities.length > 0) {
                            if (!data.allData) {
                                data.allData = []
                            }

                            // Restraining to filtered ids if there are some
                            if (filteredIds) {
                                const matchingLocalEntities = await this.offlineDataFetcher.searchAll(query, localEntities, await this.getLocallyUpdatedCachedObjectsAsS(), true).pipe(first()).toPromise();
                                localEntities = matchingLocalEntities.allData;
                            }

                            data.allData.push(...localEntities);
                            data.totalSize += localEntities.length;
                        }
                        return data
                    }
                )
            )
        } else {
            return this.locallyCreatedEntitiesToData$()
                .pipe(
                    switchMap(
                        async data => {
                            const locallyUpdatedData = await this.getLocallyUpdatedCachedObjectsAsS();
                            return this.offlineDataFetcher.searchAll(query, data, locallyUpdatedData, undefined)
                        }
                    ),
                    switchMap(
                        data => from(data)
                    ),
                    this.mergeUpdateCacheDataToSearchResults.bind(this)
                )
        }
    }

    public searchAllOffline(queryText: string | null, sortObject: any, pageSize: number = 15, types?: string[], page: number = 0, advancedSearches: any = null, filteredIds?: string[], pathAndValueToCheck?: any, searchEndpointOverride: string | null = null, options: DataStoreOptions = {}) {
        const searchTypes = types || this.typesForSearch
        const query = {
            queryText,
            sortObject,
            types: searchTypes,

            advancedSearches,
            excludedIds: null,
            filteredIds,
            group: this.userGroupId,
            language: FALLBACK_LOCALE,
            page,
            pageSize,
            pathAndValueToCheck,
            searchOnArchived: false,
            searchOnVersioned: false
        }
        return this.locallyCreatedEntitiesToData$()
                .pipe(
                    switchMap(
                        async data => {
                            const locallyUpdatedData = await this.getLocallyUpdatedCachedObjectsAsS();
                            return this.offlineDataFetcher.searchAll(query, data, locallyUpdatedData, true)
                        }
                    ),
                    switchMap(
                        data => from(data)
                    ),
                    this.mergeUpdateCacheDataToSearchResults.bind(this)
                )
    }

    /**
     * Performs a simple text search  and returns the first result
     * Useful to search on exact item identifier for example
     * @param queryText 
     */
    public searchOneWithText(queryText: string, types: string[]): Promise<S> {
        return this.searchAll(queryText, null, undefined, types, 0, undefined, undefined, undefined, undefined, undefined).pipe(
            pluck('allData'),
            first(),
            map(
                searchResults => searchResults[0]
            )
        )
            .toPromise()
    }

    public searchTitles(types: string[], filterText: string, pageNumber: number = 0, defaultFilter: string = "", size: number = 10, filterIds?: string[], esPath?: any, language: string = FALLBACK_LOCALE) {
        if (window.navigator.onLine) {
            let searchOptions: any = {
                defaultFilter,
                esPath,
                filterText,
                language,
                pageNumber,
                size,
                types,
            }

            if (filterIds) {
                searchOptions = { ...searchOptions, filterIds }
            }
            return this.proxy.fetch("/indexSearch/title", searchOptions, "POST")
        } else {
            return from(this.offlineDataFetcher.searchTitles(types, filterText, pageNumber, defaultFilter, size, filterIds, esPath, language))
        }
    }

    public searchTitle(types: string[], id: string) {
        return this.searchTitles(
            types,
            "",
            0,
            undefined,
            1,
            [id]
        )
    }

    public notifyOn(pattern: RegExp) {
        return this.cache.notifyOn(pattern);
    }

    public updateOfflineMode(id: string, body: Partial<T>) {
        this.offlineDataUpdateCacher.storeBeforePushing(id, body as Partial<T> & {id: string});
        return this.fetchOneOfflineMode(id);
    }

    public update(id: string, body: Partial<T>, endpointOverride?: string) {
        // If online, update directly in backend
        if (window.navigator.onLine && !OfflineDataFetcher.isTemporaryId(id)) { // differentiate locally created entities from other entities
            // Online update
            const endpoint = endpointOverride || this.endpoint;
            const uri = `${endpoint}/${id}`;
            if(_.get(body,'associatedMedias.medias', []) === null){
                _.set(body,'associatedMedias.medias', [])
            }
            const localMedia = _.get(body,'associatedMedias.medias', []).filter(r => r.startsWith("local:"))
            const uploadedMedia = _.get(body,'associatedMedias.medias', []).filter(r => !r.startsWith("local:"))
            _.set(body,'associatedMedias.medias', uploadedMedia)
            return this.proxy
                .fetch(uri, undefined, 'PUT', body, true)
                .pipe(
                    switchMap(response => this.fetchOne(id)),
                    // Remove corresponding in updateCache (if exists) to avoid two versions of the entity
                    tap(async data => {
                        const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(id)
                        if (cachedUpdatedValue) {
                            this.offlineDataUpdateCacher.removeCachedValue(id)
                        }
                        localMedia.forEach(element => {
                            _.get(body,'associatedMedias.medias', []).push(element)
                        });
                        // update cached data from fetch to avoid to versions of the entitiy
                        this.offlineDataFetcher.updateOne(id, data as T);
                    }),
                    switchMap(() => this.fetchOne(id, true)));
        }
        // If offline, store in cache and return cached data merged with updated data
        else {
            return this.updateOfflineMode(id, body);
        }
    }

    public create(createBody?: Partial<T>, body?: Partial<T>): Observable<T> {
        const uri = `${this.endpoint}`;
        if (window.navigator.onLine || !body) {
            return this.proxy.fetch(uri, createBody, 'POST').pipe(
                concatMap(response => {
                    if (!body) {
                        return of(response);
                    }
                    // In case of a locally created entity, replacing temporary id by the one from db
                    const id: string = _.get(body, "id")
                    if (id && OfflineDataFetcher.isTemporaryId(id)) {
                        Object.defineProperty(body, "id", { value: response.id })
                    }
                    return this.update(response.id, lodashMerge(response, body));
                }),
                switchMap(({ id }) => this.fetchOne(id)),
            );
        } else { // Offline mode
            // the '_' prefix of the id identifies the id as temporary in the cache keys
            return this.createOffline(body);
        }
    }

    public createOffline(body: Partial<T>) {
        const tempId = '_' + Math.random().toString(36).substr(2, 9) + Math.random().toString(36).substr(2, 9);
        const newBody: Partial<T> & {id: string} = Object.assign({id: ''}, body);
        newBody.id = tempId;

        this.offlineDataUpdateCacher.storeBeforePushing(tempId, newBody);
        return of('_').pipe(
            switchMap(() => this.fetchOneOfflineMode(tempId)),
            tap(item => {
                if (!item) {
                    throw new Error('Item not yet available')
                }
            }),
            retryWhen(errors => errors.pipe(() => timer(1000)))
        );
    }

    public duplicate(entityToDuplicateId: string, createBody?: Partial<T>, body?: Partial<T>) {

        const duplicateUri = `${this.endpoint}/duplicate/${entityToDuplicateId}`;
        const getUri = `${this.endpoint}/${entityToDuplicateId}`

        return this.proxy.fetch(duplicateUri, createBody, 'POST').pipe(
            concatMap(response => {
                if (!body) {
                    return of(response);
                }
                return this.update(response.id, lodashMerge(response, body));
            }),
            switchMap(({ id }) => this.fetchOne(id))
        );
    }

    /**
     * Returns an observable that emits a boolean value for 
     * each push of an entity in the cache (true for success, false for failure)
     */
    public async pushCachedValues(pushNewEntities: boolean = true): Promise<Observable<boolean>> {
        this.globalPushing = true;
        let successCounter = 0;
        // getting ids of entities in cache)
        let allIds: string[] = await this.offlineDataUpdateCacher.getCachedObjectsKeys();
        // Remove new entities if necessary
        if (!pushNewEntities) {
            allIds = allIds.filter(id => !id.includes('_'))
        }
        const idsObs = of(allIds);
        return idsObs.pipe(
            // Retrieving data to update from cache
            switchMap(
                async ids => {
                    const dataToUpdate: Array<CachedEntityData<T> & {mediaSubject: ReplaySubject<any>}> = [];
                    for (const id of ids) {
                        // Check if pushing has been interrupted
                        if (this.globalPushing) {
                            const sanitizedId = this.offlineDataUpdateCacher.getSanitizedId(id);
                            const body = await this.offlineDataUpdateCacher.getCachedValue(sanitizedId);

                            // Cleaning unwanted undefined cached values
                            if (sanitizedId === "undefined") {
                                await this.offlineDataUpdateCacher.removeCachedValue(sanitizedId);
                            }

                            const fixSubelement = ((item: { [key: string]: any | any[] }) => {
                                for (const [key, value] of Object.entries(item)) {
                                    if (typeof value === 'object' && _.isEmpty(value)) {
                                        _.set(item, key, null)
                                    } else if (Array.isArray(value)) {
                                        value.forEach(e => fixSubelement(e))
                                        _.set(item, key, value.filter(o => !(typeof o === 'object' && o !== null && _.isEmpty(o))))
                                    } else if (typeof value === 'object' && value !== null) {
                                        fixSubelement(value)
                                    }
                                }
                            })
                    
                            fixSubelement(body as unknown as {[key: string]:any})

                            const mediaSubject = new ReplaySubject()
                            if (this.hasAssociatedMedias(body)) {
                                let localMedias = _.get(body.associatedMedias, 'medias', []).filter(m => m.startsWith('local'))
                                _.remove(_.get(body.associatedMedias, 'medias', []), (m: string) => m.startsWith('local'))

                                if (body.associatedMedias.first) {
                                    localMedias.unshift(body.associatedMedias.first)
                                    localMedias = _.uniq(localMedias)
                                }

                                const mediaTransferHelper = new MediaTransferHelper()
                                const mediaObservable = merge(from(localMedias)).pipe(
                                    mergeMap(e => this.cache.read(e) as unknown as string),
                                    map(res => {
                                        const byteString = atob(res.split(',')[1])
                                        const arrayBuffer = new ArrayBuffer(byteString.length)
                                        const intArray = new Uint8Array(arrayBuffer)
                                        
                                        for (let i = 0; i < byteString.length; i++) {
                                            intArray[i] = byteString.charCodeAt(i)
                                        }

                                        return new Blob([intArray], {type: 'image/jpeg'})
                                    }),
                                    map(res => mediaTransferHelper.uploadMedia('image/jpg', new File([res], `temp.${res.type.split('/').pop()}`), 'data:')),
                                    mergeMap(res => mediaTransferHelper.downloadMedia(res)),
                                    filter(res => res.newMediaId),
                                    take(localMedias.length)
                                )

                                mediaObservable.subscribe(mediaSubject)
                            }
                            

                            dataToUpdate.push({ id: sanitizedId, body, mediaSubject });
                        }
                    }
                    // When there are only locally created entities and 'pushNewEntities' is false, we have to return something
                    // to notify that the push is finished eventhough nothing has been pushed directly (ex : auditRecords)
                    if (!pushNewEntities && dataToUpdate.length === 0) {
                        return {} as any
                    }
                    return of(...dataToUpdate); // quand [] = :'(
                }
            ),
            // this switchMap is required to cancel the async of the previous callback, which
            // generates a Observable instead of the wanted data directly
            switchMap(data => data),
            tap((a) => {
                successCounter++
            }),
            // Performing updates
            concatMap(
                (e) => this.pushUpdate(e) 
            ),
        )
    }

    public pushUpdate(dataToUpdate) {
        if (dataToUpdate.id && dataToUpdate.body) {
            this.synchronizationMap[dataToUpdate.id] = 'inProgress'
            return this.update(
                dataToUpdate.id,
                dataToUpdate.body
            )
                .pipe(
                    first(),
                    // Handle success or error if the update and emit boolean
                    catchError(e => of(Error('Error during update...'))),
                    switchMap(
                        response => {
                            if (!(response instanceof Error)) {
                                if (OfflineDataFetcher.isTemporaryId(dataToUpdate.id)) {
                                    this.synchronizationMap[dataToUpdate.id] = (response as unknown as {id: string}).id
                                }
                                this.offlineDataUpdateCacher.removeCachedValue(dataToUpdate.id);
                                return of(true)
                            }
                            return of(false);
                        }
                    )
                )
        }
        else {
            return of(true);
        }
    }

    public hasAssociatedMedias(item): item is {associatedMedias: {medias?: string[], first: string}} {
        return !!item.associatedMedias?.medias
    }

    public async getNbCachedObjects(countLocallyCreated: boolean = true) {
        return await this.offlineDataUpdateCacher.getNbCachedObjects(countLocallyCreated);
    }

    public async getObjectInUpdateCache(id: string) {
        return this.offlineDataUpdateCacher.getCachedValue(id)
    }

    public async removeObjectFromUpdateCache(id: string) {
        return this.offlineDataUpdateCacher.removeCachedValue(id)
    }

    public deleteModifications(id: string) {
        return from(this.offlineDataUpdateCacher.fetchOneFromId(id)).pipe(map(e => {
            _.get((e), 'associatedMedias.medias', [])
                .filter(media => media.startsWith('local'))
                .forEach(media => MediaTransferHelper.getInstance().deleteImageInCache(media))

            return this.offlineDataUpdateCacher.deleteBeforePushing(id);
        }))
    }

    public delete(id: string) {
        if (OfflineDataFetcher.isTemporaryId(id)) {
            return this.deleteModifications(id);
        }
        const uri = `${this.endpoint}/${id}`;

        const network$ = this.proxy.fetch(uri, undefined, "DELETE", undefined, undefined).pipe(
            last(),
            catchError(error => (fetch.isNetworkError(error) ? EMPTY : throwError(error))),
        );
        return network$.pipe(distinctUntilChanged(isEqual));
    }

    public async synchronize(pageIndex: number, clearBefore?: boolean, firstCall: boolean = true) {
        if (firstCall) {
            this.executionStartTime = moment.now();
            this.abortSync = false;
            this.currentNbSychronized = 0;
            this.syncing = true;
            this.saveStats(DataSynchronisationStatus.SUCCESS);
        }
        if (!this.abortSync) {
            // Freeing last subscription
            if (this.currentSynchroSubscription) {
                this.currentSynchroSubscription.unsubscribe()
            }

            if (clearBefore) {
                this.offlineDataFetcher.clear()
                this.currentNbSychronized = 0;
            }

            this.currentSynchroSubscription =
                iif(() => window.navigator.onLine,
                    this.fetchPage(pageIndex),
                    throwError("Synchronization cannot be performed in offline mode")
                )
                    .pipe(
                        first(),
                        catchError(
                            (error) => {
                                this.saveStats(DataSynchronisationStatus.ERROR);
                                this.syncing = false;
                                dataSynchroEventBus$.next({
                                    name: this.mainCacheKey,
                                    status: DataSynchronisationStatus.ERROR,
                                })
                                throw error;
                            }
                        )
                    )
                    .subscribe(
                        async searchResponse => {

                            // Retrieving total number of results for stats
                            if (this.maxNbToSynchronize === Infinity) {
                                this.maxNbToSynchronize = searchResponse.totalSize
                            }

                            // Appending results if there are ones, ending syncing otherwise
                            if (searchResponse.results.length > 0) {
                                await this.offlineDataFetcher.append(searchResponse.results)
                                if (this.emulateSearch) {
                                    this.currentNbSychronized += 1;
                                } else {
                                    this.currentNbSychronized += searchResponse.results.length;
                                }
                                this.saveStats(DataSynchronisationStatus.SUCCESS);
                                this.synchronize(pageIndex++, false, false)
                            } else {
                                dataSynchroEventBus$.next({
                                    name: this.mainCacheKey,
                                    status: DataSynchronisationStatus.SUCCESS,
                                })
                                this.saveStats(DataSynchronisationStatus.SUCCESS);
                                this.syncing = false;
                            }
                        }
                    )
            pageIndex++;
        }
        else {
            this.saveStats(DataSynchronisationStatus.ABORTED);

            this.abortSync = false;
            this.syncing = false;
            dataSynchroEventBus$.next({
                name: this.mainCacheKey,
                status: DataSynchronisationStatus.ABORTED,
            })
            this.saveStats(DataSynchronisationStatus.ABORTED)
        }
    }

    public abortPush() {
        this.globalPushing = false;
    }

    public abortSynchronisation() {
        this.abortSync = true;
    }

    public async fetchNbEntitiesInUpdateCache() {
        this.nbEntitiesInUpdateCache = await this.getNbCachedObjects(true)
        this.nbCreatedEntitiesInUpdateCache = this.nbEntitiesInUpdateCache - await this.getNbCachedObjects(false)
    }

    public fetchOneFromPredicate(predicate: (data: T) => boolean) {
        if (window.navigator.onLine) {
            throw new Error("This method is not meant to be called in an online context");
        } else {
            return this.offlineDataFetcher.fetchOneFromPredicate(predicate)
        }
    }

    public recursiveGenericMerge(base, newFields) {
        if (!base) {
            return newFields
        }
        if (!newFields) {
            return base
        }
        if (typeof base !== 'object' || typeof newFields !== 'object') {
            return newFields || base
        }
        let keys = Object.keys(newFields)
        Object.keys(base).forEach((e) => keys.push(e))
        keys = _.uniq(keys)
        const mergeResult = {}
        for (const field of keys) {
            if ((base[field] && Array.isArray(base[field])) || (newFields[field] && Array.isArray(newFields[field]))) {
                mergeResult[field] = _.zip(base[field], newFields[field]).map(e => this.recursiveGenericMerge(...e))
            } else if ((base[field] && typeof base[field] === 'object') || (newFields[field] && typeof newFields[field] === 'object')) {
                mergeResult[field] = this.recursiveGenericMerge(base[field], newFields[field])
            } else {
                mergeResult[field] = newFields[field] || base[field]
            }
        }
        return mergeResult
    }

    public emptySynchronizationMap(): void {
        this.synchronizationMap = {}
    }

    public getUpdatedValue(localId: string): string | undefined {
        return this.synchronizationMap[localId]
    }

    public addImage(element, image) {
        this.proxy.fetch(`/${this.endpoint}/addMedia/${element.id}`,{idMedia: image.newMediaId}, 'PUT')
    }

    // tslint:disable-next-line:member-ordering
    protected convertTtoS(toConvert: T): S {
        return toConvert as any as S;
    }

    // tslint:disable-next-line:member-ordering
    protected convertStoT(toConvert: S): T {
        return toConvert as any as T;
    }

    protected initializeDataFetcher() {
        this.offlineDataFetcher = new OfflineDataFetcher<T, S>(this.sanitizedIdentifer);
    }

    protected initializeDataPusher() {
        this.offlineDataUpdateCacher = new OfflineDataUpdateCacher<T>(this.sanitizedIdentifer);
    }

    protected mergeUpdateCacheDataToOneResult(source$: Observable<T>) {
        return source$.pipe(switchMap(
            // retrieving cache data that has not been pushed to keep the vue up to date
            async (data: any) => {
                const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(data.id);
                if (cachedUpdatedValue) {
                    data = lodashMerge(data, cachedUpdatedValue)
                }
                return data
            }
        ))
    }

    /**
     * Returns an the locally created entities as an array
     */
    private async getLocallyCreatedCachedObjectsAsS(): Promise<S[]> {
        return (await this.offlineDataUpdateCacher.getLocallyCreatedCachedObjects()).map(this.convertTtoS)
    }

    private async getLocallyUpdatedCachedObjectsAsS(): Promise<S[]> {
        return (await this.offlineDataUpdateCacher.getLocallyUpdatedCachedObjects()).map(this.convertTtoS)
    }

    /**
     * Returns an observable emitting the locally created entities as an array
     */
    private locallyCreatedEntitiesToData$(): Observable<S[]> {
        return from(this.getLocallyCreatedCachedObjectsAsS())
    }

    private saveStats(status: DataSynchronisationStatus) {
        this.offlineDataFetcher.saveStats({
            currentNbSychronized: this.currentNbSychronized,
            lastSynchroDate: moment.now(),
            lastSynchroDuration: moment().diff(this.executionStartTime),
            lastSynchroStatus: status,
            maxNbToSynchronize: this.maxNbToSynchronize,
        });
    }

    /**
     * Pipeable operator thats merges data in the update cache that have not been pushed yet
     * into the search result (either from sync-cache or directly network result)
     * Only for results of a search request
     * @param source$ 
     */
    private mergeUpdateCacheDataToSearchResults(source$: Observable<SearchResponse<S>>) {
        return source$.pipe(switchMap(
            // retrieving cache data that has not been pushed to keep the vue up to date
            async (data: SearchResponse<S>) => {
                const results = data.allData;
                if (results) {
                    for (let result of results) {
                        const cachedUpdatedValue = await this.offlineDataUpdateCacher.getCachedValue(_.get(result, 'id'));
                        if (cachedUpdatedValue) {
                            result = this.mergeCacheToEntity(result, cachedUpdatedValue)
                        }
                    }
                }
                return data
            }
        ))
    }

    private mergeCacheToEntity(entity: any, cachedValue: any) {
        const cachedCurrentState = _.get(cachedValue, 'workflowSchema.currentState')
        const uncachedMediaFirst = _.get(entity, 'associatedMedias.first')
        const mergedResult = lodashMerge(entity, cachedValue)

        if (cachedCurrentState) {
            _.set(mergedResult, 'workflowSchema.currentState', cachedCurrentState);
        }

        if (uncachedMediaFirst) {
            _.set(mergedResult, 'associatedMedias.first', uncachedMediaFirst);
        }

        return mergedResult;
    }

    private cache$(key: string): Observable<T[]> {
        return this.cache.read(key).pipe(filter(data => data !== undefined)) as Observable<T[]>;
    }

    private fetchPage(pageIndex: number): Observable<SearchResponse<S>> {
        return this.synchronizeAll(
            "",
            null,
            SEARCH_PAGE_SIZE,
            this.typesForSearch,
            pageIndex,
            undefined,
            undefined,
            undefined,
            { emulateSearch: this.emulateSearch }
        )
    }
}
